import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { combineLatest, Observable, of } from 'rxjs';
import { concatMap, first, map } from 'rxjs/operators';
import { SpiderchartXAxis } from '../../@theme/components/charts/kpi-spiderchart/xaxis-configuration';

import { KPI } from '../api';
import { IdNameMixin } from '../api/models';
import { NotationsService } from './notations.service';
import { RoadmapNotationsService } from './roadmap-notations.service';
import { RoadmapService } from './roadmap.service';
import { ScopeService } from './scope.service';

/**
 * Service that manages spiderchart configuration.
 */
@Injectable()
export class SpiderChartConfigurationService {

  constructor(
    private notationsService: NotationsService,
    private roadmapService: RoadmapService,
    private roadmapNotationsService: RoadmapNotationsService,
    private scope: ScopeService,
    private translate: TranslateService,
  ) { }


  /**
   * Recover spiderchart instances using spiderchart configurations
   *
   * @param xAxisConfigurations
   * @param scenarioId
   * @param technicalStateQuestionId
   * @param benchmarkPerimeters
   * @param benchmarkScenario
   */
  public getXAxisInstances(
    xAxisConfigurations: SpiderchartXAxis.Configuration[],
    scenarioId: number,
    technicalStateQuestionId: number,
    benchmarkPerimeters?: KPI.Perimeter[],
    benchmarkScenario?: KPI.InvestmentScenario,
  ): Observable<SpiderchartXAxis.Instance[]> {
    return combineLatest(
      xAxisConfigurations.map(
        (configuration) => this.getXAxisInstance(
          configuration,
          scenarioId,
          technicalStateQuestionId,
          benchmarkPerimeters,
          benchmarkScenario,
        ),
      ),
    );
  }

  /**
   * Get an instance of SpiderchartXAxis using SpiderchartXAxis configuration
   *
   * @param configuration
   * @param scenarioId
   * @param technicalStateQuestionId
   * @param benchmarkPerimeters
   * @param benchmarkScenario
   */
  private getXAxisInstance(
    configuration: SpiderchartXAxis.Configuration,
    scenarioId: number,
    technicalStateQuestionId: number,
    benchmarkPerimeters?: KPI.Perimeter[],
    benchmarkScenario?: KPI.InvestmentScenario,
  ): Observable<SpiderchartXAxis.Instance> {
    if (configuration.type === SpiderchartXAxis.Type.EMPTY) {
      return this.translate.get('pages.common.charts.kpi-spiderchart.empty_axis').pipe(
        map<string, SpiderchartXAxis.Instance>((label) => ({
          label,
          type: SpiderchartXAxis.Type.EMPTY,
          data: [],
          benchmarkData: [],
        })),
      );
    } else if (configuration.type === SpiderchartXAxis.Type.TECHNICAL_STATE) {
      const [notation$] = this.getTechnicalStateNotation(configuration, scenarioId, technicalStateQuestionId);

      let benchmarkNotation$: Observable<KPI.BaseComputedNotation>;

      if (benchmarkPerimeters && benchmarkPerimeters.length > 0) {
        [benchmarkNotation$] = this.getTechnicalStateNotation(
          configuration,
          scenarioId,
          technicalStateQuestionId,
          benchmarkPerimeters,
        );
      } else if (benchmarkScenario) {
        [benchmarkNotation$] = this.getTechnicalStateNotation(
          configuration,
          benchmarkScenario.id,
          technicalStateQuestionId,
        );
      } else {
        benchmarkNotation$ = of(null);
      }

      return combineLatest(
        notation$,
        benchmarkNotation$,
      ).pipe(
        concatMap(([notation, benchmarkNotation]) => {
          return this.getTechnicalStateLabel(configuration, notation).pipe(
            map((label) => ({ label, notation, benchmarkNotation })),
          );
        }),
        map(({ label, notation, benchmarkNotation }) => {
          if (notation.nb_assets) {
            const notationMap = (computedNotation: KPI.BaseComputedNotation) => (year: number): number => {
              if (computedNotation.notation[year]) {
                return computedNotation.notation[year].notation;
              }
              return null;
            };

            const yearRange = R.range(notation.start_year, notation.end_year + 1);

            return {
              label,
              type: SpiderchartXAxis.Type.TECHNICAL_STATE,
              auditYear: notation.start_year,
              projectedYear: notation.end_year,
              data: yearRange.map(notationMap(notation)),
              benchmarkData: benchmarkNotation ? yearRange.map(notationMap(benchmarkNotation)) : [],
              link: [
                'insight/notations',
                ...(configuration.superCategory ? ['super-category', configuration.superCategory] : []),
                ...(configuration.category ? ['category', configuration.category] : []),
                ...(configuration.subCategory ? ['sub-category', configuration.subCategory] : []),
                ...(configuration.assetType ? ['asset-type', configuration.assetType] : []),
              ],
            };
          }
          configuration.undisplayed = true;
          return null;
        }),
      );
    } else if (configuration.type === SpiderchartXAxis.Type.ROADMAP_INDICATOR) {
      const [notation$] = this.roadmapNotationsService.getRoadmapIndicatorNotation(
        configuration.roadmapIndicator,
      );

      let benchmarkNotation$: Observable<KPI.BaseRoadmapNotation>;

      if (benchmarkPerimeters && benchmarkPerimeters.length > 0) {
        [benchmarkNotation$] = this.roadmapNotationsService.getRoadmapIndicatorNotation(
          configuration.roadmapIndicator,
          benchmarkPerimeters,
        );
      } else {
        benchmarkNotation$ = of(null);
      }

      return combineLatest(
        notation$,
        benchmarkNotation$,
        this.roadmapService.roadmapIndicators(),
      ).pipe(
        map(([notation, benchmarkNotation, roadmapIndicators]) => {
          const roadmapIndicator = roadmapIndicators.find((r) => r.id === configuration.roadmapIndicator);
          const subLabel = this.translate.instant(
            'pages.common.charts.kpi-spiderchart.nb_control_points',
            {
              value: notation.notation.done_points,
            },
          );

          return {
            label: `${roadmapIndicator.name}<br><span style="fill:#ccc">${subLabel}</span>`,
            type: SpiderchartXAxis.Type.ROADMAP_INDICATOR,
            auditYear: notation.audit_year,
            data: [notation.notation.current],
            benchmarkData: benchmarkNotation ? [benchmarkNotation.notation.current] : [],
            link: [
              'insight/notations',
              'roadmap-indicator', configuration.roadmapIndicator,
            ],
          };
        }),
      );
    }
  }

  /**
   * Recover the notation based on the spiderchart configuration
   *
   * @param configuration
   * @param scenarioId
   * @param technicalStateQuestionId
   * @param forcedPerimeters
   */
  private getTechnicalStateNotation(
    configuration: SpiderchartXAxis.TechnicalStateConfiguration,
    scenarioId: number,
    technicalStateQuestionId: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.BaseComputedNotation>, Observable<boolean>] {
    const {
      superCategory,
      category,
      subCategory,
      assetType,
    } = configuration;

    if (configuration.assetType !== undefined) {
      return this.notationsService.getAssetTypeNotation(
        assetType,
        subCategory,
        category,
        superCategory,
        scenarioId,
        [technicalStateQuestionId],
        forcedPerimeters,
      );
    } else if (configuration.subCategory !== undefined) {
      return this.notationsService.getSubCategoryNotation(
        subCategory,
        category,
        superCategory,
        scenarioId,
        [technicalStateQuestionId],
        forcedPerimeters,
      );
    } else if (configuration.category !== undefined) {
      return this.notationsService.getCategoryNotation(
        category,
        superCategory,
        scenarioId,
        [technicalStateQuestionId],
        forcedPerimeters,
      );
    } else if (configuration.superCategory !== undefined) {
      return this.notationsService.getSuperCategoryNotation(
        superCategory,
        scenarioId,
        [technicalStateQuestionId],
        forcedPerimeters,
      );
    } else {
      return this.notationsService.getGlobalNotation(
        scenarioId,
        [technicalStateQuestionId],
        forcedPerimeters,
      );
    }
  }

  /**
   * Recover technical state label from spiderchart configuration and notation
   *
   * @param configuration
   * @param notation
   */
  private getTechnicalStateLabel(
    configuration: SpiderchartXAxis.TechnicalStateConfiguration,
    notation: KPI.BaseComputedNotation,
  ): Observable<string> {
    return combineLatest(
      this.scope.superCategories$,
      this.scope.categories$,
      this.scope.subCategories$,
      this.scope.assetTypes$,
    ).pipe(
      first(),
      map(([superCategories, categories, subCategories, assetTypes]) => {
        let structure: IdNameMixin;

        if (configuration.assetType !== undefined) {
          structure = assetTypes[configuration.assetType];
        } else if (configuration.subCategory !== undefined) {
          structure = subCategories[configuration.subCategory];
        } else if (configuration.category !== undefined) {
          structure = categories[configuration.category];
        } else if (configuration.superCategory !== undefined) {
          structure = superCategories[configuration.superCategory];

        }

        const technicalStateLabel = structure?.name || '';
        let nbAssetsLabel;
        nbAssetsLabel = this.translate.instant(
          '{assetsNb} equipments',
          {
            assetsNb: notation && notation.nb_assets && notation.nb_assets[notation.start_year]
              ? (notation.nb_assets[notation.start_year].notations +
                notation.nb_assets[notation.start_year].computed +
                notation.nb_assets[notation.start_year].no_notations)
              : 0,
          },
        );
        // Differenciate Global notation legend from detailed notation legend
        if (configuration.superCategory !== undefined &&
          configuration.category === undefined &&
          superCategories[configuration.superCategory].audit_type === 1) {
          nbAssetsLabel = this.translate.instant(
            'pages.common.charts.kpi-spiderchart.global_notation',
            {
              value: notation && notation.nb_notations && notation.nb_notations[notation.start_year]
                ? (notation.nb_notations[notation.start_year].notations +
                  notation.nb_notations[notation.start_year].computed)
                : 0,
            },
          );
        }

        return `${technicalStateLabel}<br><span style="fill:#a6a6a6; text-decoration: underline;">${nbAssetsLabel}</span>`;
      }),
    );
  }

}
